import { css } from 'lit';
import * as t from '../styles/tokens.js';
import { hostReset } from '../styles/mixins.js';

export default [
  hostReset,
  css`
    /* ── Host ────────────────────────────────────────────────────────── */
    :host {
      display: flex;
      flex-direction: column;
      border-radius: var(--uwc-card-radius, ${t.radiusXl});
      overflow: hidden;
      background: var(--uwc-card-bg, ${t.surface});
      border: var(--uwc-card-border, 1px solid ${t.border});
      color: var(--uwc-card-color, ${t.text});
      font-family: inherit;
      font-size: ${t.fontSizeMd};
    }

    :host([elevated]) {
      box-shadow: var(--uwc-card-shadow, ${t.shadowMd});
      border-color: transparent;
    }

    :host([flat]) {
      border-color: transparent;
      box-shadow: none;
    }

    /* ── Header slot (image / media zone) ────────────────────────────── */
    .uwc-card__header {
      display: block;
      overflow: hidden;
    }
    .uwc-card__header::slotted(*),
    ::slotted([slot="header"]) {
      display: block;
    }
    /* The slotted image or element fills the header edge-to-edge */
    slot[name="header"]::slotted(img),
    slot[name="header"]::slotted(video) {
      width: 100%;
      display: block;
      object-fit: cover;
    }
    .uwc-card__header:empty { display: none; }

    /* ── Inner wrapper (fills host, stacks body + footer) ───────────── */
    .uwc-card__inner {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    /* ── Body ────────────────────────────────────────────────────────── */
    .uwc-card__body {
      padding: var(--uwc-card-body-padding, 1.25rem 1.5rem);
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: ${t.space3};
    }

    /* ── Title block (title + subtitle) ─────────────────────────────── */
    .uwc-card__title-block {
      display: flex;
      flex-direction: column;
      gap: ${t.space1};
    }
    .uwc-card__title-block:empty { display: none; }

    .uwc-card__title {
      font-size: var(--uwc-card-title-size, ${t.fontSizeXl});
      font-weight: ${t.fontWeightSemibold};
      color: var(--uwc-card-title-color, ${t.text});
      line-height: 1.3;
      margin: 0;
    }

    .uwc-card__subtitle {
      font-size: var(--uwc-card-subtitle-size, ${t.fontSizeMd});
      color: var(--uwc-card-subtitle-color, ${t.textSecondary});
      margin: 0;
      line-height: 1.5;
    }

    /* ── Content slot ────────────────────────────────────────────────── */
    .uwc-card__content {
      font-size: ${t.fontSizeMd};
      color: var(--uwc-card-content-color, ${t.textSecondary});
      line-height: 1.7;
    }
    .uwc-card__content:empty { display: none; }

    /* Slot content inherits body font */
    slot:not([name]) {
      display: block;
    }

    /* ── Footer slot ─────────────────────────────────────────────────── */
    .uwc-card__footer {
      padding: var(--uwc-card-footer-padding, .875rem 1.5rem 1.25rem);
      border-top: var(--uwc-card-footer-border, 1px solid ${t.border});
      display: block;
    }
    .uwc-card__footer:empty { display: none; }

    /* ── Horizontal layout ───────────────────────────────────────────── */
    :host([horizontal]) {
      flex-direction: row;
    }
    :host([horizontal]) .uwc-card__header {
      flex-shrink: 0;
      width: var(--uwc-card-horizontal-header-width, 220px);
    }
    :host([horizontal]) slot[name="header"]::slotted(img),
    :host([horizontal]) slot[name="header"]::slotted(video) {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    :host([horizontal]) .uwc-card__inner {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
    }
    :host([horizontal]) .uwc-card__footer {
      margin-top: auto;
    }
  `,
];
